import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2, X } from "lucide-react";
import { API } from "../../App";

const STYLES = [["wall", "THE WALL look"], ["engraving", "Vintage engraving"], ["night", "Night life"], ["gem", "Gemstone"], ["none", "No style hint"]];

// Admin-only: describe a picture → gpt-image-1 → saved like an upload → onDone(url). Takes 20–60 s.
export const GenerateImageButton = ({ onDone, purpose = "", compact = false, defaultStyle = "wall" }) => {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState(defaultStyle);
  const [quality, setQuality] = useState("medium");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (prompt.trim().length < 3) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/ai/image`, { prompt, style, quality, purpose }, { withCredentials: true, timeout: 180000 });
      onDone(r.data.url); toast.success("Image made — it's saved with your uploads"); setOpen(false); setPrompt("");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't generate that image"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={`inline-flex items-center gap-1 font-semibold text-[#7C3AED] hover:text-[#5b21b6] ${compact ? "text-[10px]" : "text-sm"}`} title="Make a picture with AI (admin only)" data-testid="ai-image-open">
        <Sparkles className={compact ? "h-3 w-3" : "h-4 w-4"} /> Generate
      </button>
      {open && (
        <div className="fixed inset-0 z-[10060] bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={() => !busy && setOpen(false)} data-testid="ai-image-dialog">
          <div className="w-full max-w-lg rounded-2xl bg-white p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="m-0 text-base font-bold text-[#1A1A1A] inline-flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-[#7C3AED]" /> Generate a picture</h3>
              <button type="button" onClick={() => !busy && setOpen(false)} className="p-1 text-[#5C5C5C]" aria-label="Close" data-testid="ai-image-close"><X className="h-4 w-4" /></button>
            </div>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} placeholder="Describe it — e.g. an open wooden treasure chest on a dock at dusk, gems glowing inside, Puget Sound behind" className="w-full rounded-lg border border-[#E2E0DC] p-2.5 text-sm" data-testid="ai-image-prompt" />
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <select value={style} onChange={(e) => setStyle(e.target.value)} className="rounded border border-[#E2E0DC] px-2 py-1.5" data-testid="ai-image-style">{STYLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
              <select value={quality} onChange={(e) => setQuality(e.target.value)} className="rounded border border-[#E2E0DC] px-2 py-1.5" data-testid="ai-image-quality"><option value="low">Quick draft</option><option value="medium">Standard</option><option value="high">Best (slow)</option></select>
              <span className="text-[#5C5C5C]">Square 1024px · 20–60 s · uses your Emergent key</span>
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} disabled={busy} className="rounded-full px-4 py-2 text-sm font-semibold text-[#5C5C5C]" data-testid="ai-image-cancel">Cancel</button>
              <button type="button" onClick={go} disabled={busy || prompt.trim().length < 3} className="rounded-full bg-[#7C3AED] text-white px-5 py-2 text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="ai-image-go">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Painting…</> : <><Sparkles className="h-4 w-4" /> Generate</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
